import { useState, useEffect, useCallback } from 'react'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'

import SVGWrapper from '../SVGWrapper'
import { ArrowDown } from '../../assets/icons'
import { MOBILE } from '../../guided-toolbox/utils/breakpoints'

// Default minimum remaining scroll distance before the button is shown. Callers
// with reserved dead space at the bottom (e.g. an overlaying footer) can raise
// this via the `threshold` prop so that space alone doesn't trigger the button.
const DEFAULT_THRESHOLD = 24

// Keep the button centered above the footer in both layouts. `absolute` anchors
// it to the nearest positioned ancestor (e.g. the chat's content column) instead
// of the viewport, so it centers within that column rather than the whole window.
const Button = styled.button`
  /* absolute: anchored just above an in-flow footer within a positioned wrapper.
     fixed: floats above the viewport-anchored footer of the page-scroll layout. */
  position: ${p => (p.absolute ? 'absolute' : 'fixed')};
  left: 50%;
  bottom: ${p => (p.absolute ? '16px' : '130px')};
  transform: translateX(-50%);

  /* Below the footer (z-index 10) and the drawer overlay so both cover it. */
  z-index: 9;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: 1px solid #e6e9ea;
  background: #ffffff;
  box-shadow: 0 2px 7px 0 rgba(0, 0, 0, 0.2);
  cursor: pointer;
  transition: 0.2s;

  ${MOBILE} {
    bottom: ${p => (p.absolute ? '16px' : '110px')};
  }

  &:hover {
    background: ${p => p.theme.primaryLighter ?? '#d4ebf2'};
    border-color: ${p => p.theme.primaryColor};
  }
`

/**
 * Shows a scroll-to-bottom button while the tracked container has content
 * below the viewport. Defaults to page scrolling; pass `getScrollElement`
 * to track a custom scroll container.
 */
const ScrollToBottomButton = ({
  getScrollElement,
  watch,
  absolute,
  threshold = DEFAULT_THRESHOLD,
  getBottomAnchor,
  bottomInset = 0,
}) => {
  const [visible, setVisible] = useState(false)

  const resolveElement = useCallback(
    () =>
      getScrollElement ? getScrollElement() : document.scrollingElement || document.documentElement,
    [getScrollElement]
  )

  // How far the real content extends below the visible area. When a bottom
  // anchor is given, measure to that element's bottom (ignoring reserved dead
  // space like a pin spacer); `bottomInset` keeps content clear of an overlaying
  // footer. Otherwise fall back to raw scrollHeight.
  const remainingBelow = useCallback(
    element => {
      const anchor = getBottomAnchor?.()
      if (anchor) {
        const anchorBottom =
          anchor.getBoundingClientRect().bottom - element.getBoundingClientRect().top
        return anchorBottom - (element.clientHeight - bottomInset)
      }
      return element.scrollHeight - element.scrollTop - element.clientHeight
    },
    [getBottomAnchor, bottomInset]
  )

  const updateVisibility = useCallback(() => {
    const element = resolveElement()
    if (!element) return
    setVisible(remainingBelow(element) > threshold)
  }, [resolveElement, remainingBelow, threshold])

  // `watch` forces an overflow re-check when external content changes.
  useEffect(() => {
    const element = resolveElement()
    updateVisibility()

    // Page scrolling emits on window; custom containers emit on the element.
    const usesCustomElement =
      element && element !== document.scrollingElement && element !== document.documentElement
    const scrollTarget = usesCustomElement ? element : window

    scrollTarget.addEventListener('scroll', updateVisibility, { passive: true })
    window.addEventListener('resize', updateVisibility)

    // Re-check when content changes the tracked area's size. (ResizeObserver
    // callbacks are globally deferred to an animation frame in index.js, so this
    // can't trip the browser's "ResizeObserver loop" notice.)
    let observer
    const observeTarget = usesCustomElement ? element : document.body

    if (typeof ResizeObserver !== 'undefined' && observeTarget instanceof Element) {
      observer = new ResizeObserver(updateVisibility)
      observer.observe(observeTarget)

      // Also watch the container's children because they can grow without changing
      // the container's size.
      if (usesCustomElement) {
        Array.from(element.children).forEach(child => observer.observe(child))
      }
    }

    return () => {
      scrollTarget.removeEventListener('scroll', updateVisibility)
      window.removeEventListener('resize', updateVisibility)
      observer?.disconnect()
    }
  }, [resolveElement, updateVisibility, watch])

  if (!visible) return null

  const scrollToBottom = () => {
    const element = resolveElement()
    if (!element) return
    // Scroll only far enough to reveal the real content bottom above the footer,
    // never into the reserved dead space below it.
    const delta = getBottomAnchor?.() ? remainingBelow(element) : element.scrollHeight
    const top = getBottomAnchor?.() ? element.scrollTop + delta : delta
    element.scrollTo({ top, behavior: 'smooth' })
  }

  return (
    <Button
      type='button'
      absolute={absolute}
      onClick={scrollToBottom}
      aria-label='Scroll to bottom'>
      <SVGWrapper svg={ArrowDown} size='custom30' />
    </Button>
  )
}

ScrollToBottomButton.propTypes = {
  /** Returns the scroll container to track; defaults to page scrolling. */
  getScrollElement: PropTypes.func,
  /** Changing this value triggers an overflow re-check. */
  watch: PropTypes.any,
  /** Anchor to the nearest positioned ancestor instead of the viewport. */
  absolute: PropTypes.bool,
  /** Remaining-scroll distance (px) that must be exceeded before showing. */
  threshold: PropTypes.number,
  /**
   * Returns the element whose bottom is the "real" content end. When provided,
   * visibility and scrolling ignore any dead space below it (e.g. a pin spacer).
   */
  getBottomAnchor: PropTypes.func,
  /** Px of overlaying chrome (e.g. footer) to keep the content clear of. */
  bottomInset: PropTypes.number,
}

export default ScrollToBottomButton
